/** Private work API. Domain objects exist only after endpoint decryption; opaque revisions go to the relay. */
import {
  type ProjectConfig,
  parseProjectConfig,
  stringifyProjectConfig,
  validateProjectConfig,
} from '@viko/project-config'
import {
  type CommandPayload,
  PrivateEntityId as Id,
  PrivateIdeaRecord as ideaSchema,
  type PrivateEntityKind,
  PrivateProjectBinding,
  PrivateProjectRecord as projectSchema,
  PrivateRuleRecord as ruleSchema,
  PrivateTaskPatch as taskPatch,
  PrivateTaskRecord as taskSchema,
  PrivateWorkstreamRecord as wsSchema,
} from '@viko/protocol'
import { resolveRunModel } from '@viko/scheduler'
import type { z } from 'zod'
import { slugify } from './account-projects.js'
import type { Api } from './api.js'
import type { BrowserEntities } from './encrypted-entities.js'
import { machineConfigKey } from './machine-key.js'
import type {
  Idea,
  Machine,
  Project,
  ProjectConfigLite,
  Rule,
  RunSummary,
  TaskDetail,
  WorkstreamDetail,
} from './types.js'
import { entityRuns, mergeRuns, projectWorkstream, type RunEvidence } from './work-projection.js'

/**
 * g18: model, effort and the header reason for a private task run. A model or effort the user
 * picked (StartSession, a bark's model constraint) and resumed sessions are never overridden.
 */
export function taskRunModel(
  payload: Pick<
    CommandPayload<'run.start'>,
    'agent' | 'model' | 'effort' | 'resumeNativeSessionId'
  >,
  task: z.infer<typeof taskSchema> | undefined,
  config: Pick<ProjectConfig, 'routing' | 'execution'>,
): { model?: string; effort?: string; modelReason?: string } {
  if (!task || payload.model || payload.effort || payload.resumeNativeSessionId) return {}
  const c = resolveRunModel({
    agent: payload.agent === 'codex' ? 'codex' : 'claude',
    task: {
      title: task.title,
      labels: task.labels,
      ...(task.tier ? { tier: task.tier } : {}),
      ...(task.model ? { model: task.model } : {}),
      ...(task.modelReason ? { modelReason: task.modelReason } : {}),
      ...(task.routing?.model ? { userModel: task.routing.model } : {}),
    },
    hints: config.routing.hints,
    ...(config.execution ? { execution: config.execution } : {}),
  })
  return {
    ...(c.model ? { model: c.model } : {}),
    ...(c.effort ? { effort: c.effort } : {}),
    modelReason: c.reason,
  }
}

function configOf(body: { yaml?: string; config?: ProjectConfigLite }): ProjectConfig {
  const parsed =
    body.yaml !== undefined ? parseProjectConfig(body.yaml) : validateProjectConfig(body.config)
  if (!parsed.ok) throw new Error(parsed.errors.map((e) => `${e.path}: ${e.message}`).join('\n'))
  return parsed.config
}

export class PrivateWork {
  constructor(
    private readonly store: () => Promise<BrowserEntities>,
    private readonly machines: () => Promise<Machine[]>,
    /** Runs this browser can vouch for; absent or failing means run state is unknown. */
    private readonly runs?: () => Promise<RunSummary[]>,
  ) {}
  private async records<T>(kind: PrivateEntityKind, schema: z.ZodType<T>) {
    return (await (await this.store()).list(kind)).map((r) => {
      if (r.value.id !== undefined && r.value.id !== r.id)
        throw new Error('Private work identity mismatch')
      const row = schema.parse({ ...r.value, id: r.id })
      if ((row as { id?: unknown }).id !== r.id) throw new Error('Private work identity mismatch')
      return row
    })
  }
  private async read<T>(kind: PrivateEntityKind, id: string, schema: z.ZodType<T>) {
    const record = await (await this.store()).read(kind, Id.parse(id)),
      row = schema.parse({ ...record.value, id: record.id })
    if (record.value.id !== undefined && record.value.id !== record.id)
      throw new Error('Private work identity mismatch')
    if ((row as { id?: unknown }).id !== record.id)
      throw new Error('Private work identity mismatch')
    return row
  }
  /** Display read: a fresh shared head equal to a verified one costs no request (read-budget). */
  private async view<T>(kind: PrivateEntityKind, id: string, schema: z.ZodType<T>) {
    const record = await (await this.store()).get(kind, Id.parse(id)),
      row = schema.parse({ ...record.value, id: record.id })
    if (record.value.id !== undefined && record.value.id !== record.id)
      throw new Error('Private work identity mismatch')
    if ((row as { id?: unknown }).id !== record.id)
      throw new Error('Private work identity mismatch')
    return row
  }
  private async save<T extends { id: string }>(
    kind: PrivateEntityKind,
    row: T,
    schema: z.ZodType<T>,
  ) {
    const value = schema.parse(row)
    await (await this.store()).write(kind, value.id, value as Record<string, unknown>)
    return value
  }
  // The opaque identifier lives in context. Preserve caller-independent timestamps across retry.
  private async create<T extends { id: string }>(
    kind: PrivateEntityKind,
    row: Omit<T, 'id'>,
    schema: z.ZodType<T>,
  ) {
    const candidate = schema.parse({ ...row, id: crypto.randomUUID() })
    const { id: _id, ...value } = candidate
    const store = await this.store()
    const record = await store.create(kind, value)
    return schema.parse({ ...record.value, id: record.id })
  }
  async assertOpen() {
    await this.store()
  }

  /** Endpoint-observed runs plus rows another trusted device mirrored into the entities. */
  private async evidence(streams: WorkstreamDetail[], tasks: TaskDetail[]): Promise<RunEvidence> {
    let seen: RunSummary[] = [],
      known = false
    try {
      if (this.runs) {
        seen = await this.runs()
        known = true
      }
    } catch {
      known = false
    }
    return { runs: mergeRuns([...seen, ...entityRuns(streams, tasks)]), known }
  }
  /** Run rows for entities that another device or a rotation recorded (evidence: `entity`). */
  async entityRunRows(): Promise<RunSummary[]> {
    const streams = await this.rawWorkstreams()
    // Only pay for task decryption when some workstream actually claims a run.
    return streams.some((w) => w.liveRun) ? entityRuns(streams, await this.rawTasks()) : []
  }
  projects = async (): Promise<Project[]> => {
    const rows = await this.records('project', projectSchema)
    const streams = await this.rawWorkstreams()
    const tasks = await this.rawTasks()
    const machines = await this.machines()
    const evidence = await this.evidence(streams, tasks)
    return rows.map((p) => {
      const config = configOf({ config: p.config as ProjectConfigLite })
      const mine = streams.filter((w) => w.projectId === p.id)
      const ts = tasks.filter((t) => t.projectId === p.id)
      return {
        ...p,
        config,
        repo: config.repos[0]?.remote ?? config.project.id,
        // account-projects: machines that report a checkout; v1 configs also match by the
        // machine key the daemon itself reports (no hub-side slug mapping).
        machines: machines
          .filter(
            (m) =>
              m.checkouts?.some((c) => c.projectId === p.id) ||
              machineConfigKey(config.machines.allowed, m),
          )
          .map((m) => m.id),
        loop: config.loopProfile,
        workstreams: mine.map((w) => {
          const view = projectWorkstream(w, ts, evidence)
          return {
            id: w.id,
            projectId: w.projectId,
            name: w.name,
            ...(w.branch ? { branch: w.branch } : {}),
            ...(w.machineId ? { machineId: w.machineId } : {}),
            ...(view.runId ? { runId: view.runId } : {}),
            state: view.state,
            tasks: view.tasks,
          }
        }),
        counts: {
          ws: mine.length,
          open: ts.filter((t) => !['done', 'cancelled'].includes(t.status)).length,
          proposed: ts.filter((t) => t.status === 'proposed').length,
        },
      }
    })
  }
  project = async (id: string) => {
    const p = (await this.projects()).find((p) => p.id === id)
    if (!p) throw new Error('Private project not found')
    return p
  }
  createProject: Api['createProject'] = async (body) => {
    const config = configOf(body),
      store = await this.store()
    const value = {
      name: config.project.name,
      status: 'active' as const,
      config,
      yaml: body.yaml ?? stringifyProjectConfig(config),
    }
    const record = await store.create('project', value)
    return {
      ...value,
      id: record.id,
      repo: config.repos[0]?.remote ?? config.project.id,
      machines: config.machines.allowed,
      loop: config.loopProfile,
      workstreams: [],
    }
  }
  // ---- account-projects: project entity v2 (name, goal, remote; shared config v2) ----
  createAccountProject: Api['createAccountProject'] = async (body) => {
    const name = body.name.trim()
    const id = slugify(name)
    const remote = body.remote?.trim() || null
    const yaml = stringifyProjectConfig({
      version: 2,
      project: { id, name },
      repos: [{ id, ...(remote ? { remote } : {}) }],
    } as never)
    const config = configOf({ yaml })
    const store = await this.store()
    const record = await store.create('project', {
      name,
      status: 'active',
      config,
      yaml,
      v: 2,
      ...(body.goal?.trim() ? { goal: body.goal.trim() } : {}),
      remote,
      checkouts: [],
    })
    return this.project(record.id)
  }
  /** Record the desired checkouts (and the remote a new repo got) on the entity. */
  recordCheckouts: Api['recordCheckouts'] = async (id, body) => {
    const prior = await this.read('project', id, projectSchema)
    const now = Date.now()
    const keep = (prior.checkouts ?? []).filter((c) => !body.machineIds.includes(c.machineId))
    await this.save(
      'project',
      {
        ...prior,
        v: 2,
        ...(body.remote && !prior.remote ? { remote: body.remote } : {}),
        checkouts: [
          ...keep,
          ...body.machineIds.map((machineId) => ({
            machineId,
            requestedAt: now,
            via: body.via,
            ...(body.statementId ? { statementId: body.statementId } : {}),
          })),
        ].slice(-64),
      },
      projectSchema,
    )
    return this.project(id)
  }
  // ---- end account-projects ----
  updateProject: Api['updateProject'] = async (id, body) => {
    const prior = await this.read('project', id, projectSchema)
    const config =
      body.yaml !== undefined || body.config !== undefined
        ? configOf(body)
        : configOf({ config: prior.config as ProjectConfigLite })
    if (config.project.id !== configOf({ config: prior.config as ProjectConfigLite }).project.id)
      throw new Error('Create a new project to use a different local repository identity')
    await this.save(
      'project',
      {
        ...prior,
        ...body,
        name: config.project.name,
        config,
        // A settings/tab-only update keeps the stored YAML byte for byte.
        yaml:
          body.yaml ?? (body.config !== undefined ? stringifyProjectConfig(config) : prior.yaml),
      },
      projectSchema,
    )
    return this.project(id)
  }
  private rawWorkstreams = async (
    q: { projectId?: string; status?: string } = {},
  ): Promise<WorkstreamDetail[]> =>
    (await this.records('workstream', wsSchema)).filter(
      (r) => (!q.projectId || r.projectId === q.projectId) && (!q.status || r.status === q.status),
    )
  private rawWorkstream = (id: string) => this.read('workstream', id, wsSchema)
  /** Stored record with liveRun replaced by authenticated run evidence. */
  private async withEvidence(rows: WorkstreamDetail[]): Promise<WorkstreamDetail[]> {
    if (!rows.length) return rows
    const tasks = await this.rawTasks(),
      evidence = await this.evidence(await this.rawWorkstreams(), tasks)
    return rows.map((w) => ({
      ...w,
      liveRun: projectWorkstream(w, tasks, evidence).liveRun,
    }))
  }
  workstreams = async (
    q: { projectId?: string; status?: string } = {},
  ): Promise<WorkstreamDetail[]> => this.withEvidence(await this.rawWorkstreams(q))
  workstream = async (id: string) =>
    (await this.withEvidence([await this.view('workstream', id, wsSchema)]))[0]!
  createWorkstream: Api['createWorkstream'] = async (body) => {
    await this.read('project', body.projectId, projectSchema)
    const now = Date.now()
    return this.create<WorkstreamDetail>(
      'workstream',
      {
        projectId: body.projectId,
        name: body.name,
        branch: body.branch ?? null,
        worktree: null,
        machineId: body.machineId ?? null,
        brief: body.brief ?? null,
        status: 'active',
        ideaId: null,
        liveRun: null,
        bulkApproved: false,
        paused: false,
        agent: null,
        counts: { open: 0, proposed: 0, done: 0 },
        createdAt: now,
        updatedAt: now,
      },
      wsSchema,
    )
  }
  updateWorkstream: Api['updateWorkstream'] = async (id, patch) =>
    this.save(
      'workstream',
      { ...(await this.rawWorkstream(id)), ...patch, updatedAt: Date.now() },
      wsSchema,
    )
  private rawTasks = async (
    q: { projectId?: string; workstreamId?: string; status?: string } = {},
  ): Promise<TaskDetail[]> =>
    (await this.records('task', taskSchema)).filter(
      (r) =>
        (!q.projectId || r.projectId === q.projectId) &&
        (!q.workstreamId || r.workstreamId === q.workstreamId) &&
        (!q.status || r.status === q.status),
    )
  /** Live run evidence overrides a stored running/blocked status; nothing else is inferred. */
  tasks = async (
    q: { projectId?: string; workstreamId?: string; status?: string } = {},
  ): Promise<TaskDetail[]> => {
    const rows = await this.rawTasks(q)
    if (!rows.some((t) => t.runId)) return rows
    const evidence = await this.evidence(await this.rawWorkstreams(), await this.rawTasks())
    const byRun = new Map(evidence.runs.map((r) => [r.runId, r]))
    return rows.map((t) => {
      const run = t.runId ? byRun.get(t.runId) : undefined
      if (
        !run ||
        (run.status !== 'running' && run.status !== 'waiting' && run.status !== 'compacting')
      )
        return t
      return { ...t, status: run.status === 'waiting' ? 'blocked' : 'running' }
    })
  }
  task = (id: string) => this.view('task', id, taskSchema)
  /** Read-modify-write paths always read the relay's latest revision. */
  private latestTask = (id: string) => this.read('task', id, taskSchema)
  private async deps(projectId: string, id: string | undefined, deps: string[]) {
    for (const dep of deps) {
      if (dep === id || (await this.view('task', dep, taskSchema)).projectId !== projectId)
        throw new Error('Invalid private task dependency')
    }
  }
  createTask: Api['createTask'] = async (body) => {
    const ws = body.workstreamId ? await this.rawWorkstream(body.workstreamId) : undefined
    const projectId = body.projectId ?? ws?.projectId
    if (!projectId || (ws && ws.projectId !== projectId))
      throw new Error('Choose a project for this task')
    await this.read('project', projectId, projectSchema)
    await this.deps(projectId, undefined, body.deps ?? [])
    const now = Date.now()
    return this.create<TaskDetail>(
      'task',
      {
        projectId,
        workstreamId: ws?.id ?? null,
        title: body.title,
        description: body.description ?? null,
        status: 'ready',
        labels: body.labels ?? [],
        deps: body.deps ?? [],
        acceptance: body.acceptance ?? [],
        provenance: 'user',
        proposed: false,
        priority: body.priority ?? 1,
        agentHint: null,
        requires: [],
        rationale: null,
        progress: null,
        blockedReason: null,
        runId: null,
        ideaId: null,
        notBefore: null,
        parked: null,
        landedAt: null,
        createdAt: now,
        updatedAt: now,
      },
      taskSchema,
    )
  }
  updateTask: Api['updateTask'] = async (id, raw) => {
    const old = await this.latestTask(id),
      patch = taskPatch.parse(raw)
    await this.deps(old.projectId, id, patch.deps ?? old.deps)
    return this.save(
      'task',
      {
        ...old,
        ...patch,
        proposed: (patch.status ?? old.status) === 'proposed',
        updatedAt: Date.now(),
      },
      taskSchema,
    )
  }
  approveTask: Api['approveTask'] = async (id) => {
    if ((await this.latestTask(id)).status !== 'proposed') throw new Error('Task is not proposed')
    return this.updateTask(id, { status: 'ready' })
  }
  approveProposed: Api['approveProposed'] = async (id, body = {}) => {
    if (body.standing) throw new Error('Private standing approval requires an endpoint grant')
    await this.rawWorkstream(id)
    const approved: string[] = []
    for (const task of await this.rawTasks({ workstreamId: id, status: 'proposed' }))
      if (!body.taskIds || body.taskIds.includes(task.id)) {
        await this.approveTask(task.id)
        approved.push(task.id)
      }
    return { approved, standing: false }
  }
  /**
   * g18-batch: ready a plan's proposed tasks with one write each, from one listing (no per-task
   * re-reads). Each write is still a compare-and-swap on that task's latest revision.
   */
  approvePlan: Api['approvePlan'] = async (planId, body = {}) => {
    const approved: string[] = []
    for (const task of await this.rawTasks({ status: 'proposed' })) {
      if (task.plan?.id !== planId || (body.taskIds && !body.taskIds.includes(task.id))) continue
      await this.save(
        'task',
        { ...task, status: 'ready', proposed: false, updatedAt: Date.now() },
        taskSchema,
      )
      approved.push(task.id)
    }
    return { approved }
  }
  rules = async (q: { projectId?: string; workstreamId?: string } = {}): Promise<Rule[]> =>
    (await this.records('rule', ruleSchema)).filter(
      (r) =>
        (!q.projectId || !r.projectId || r.projectId === q.projectId) &&
        (!q.workstreamId || !r.workstreamId || r.workstreamId === q.workstreamId),
    )
  createRule: Api['createRule'] = async (body) => {
    if (
      (body.scope === 'project' && !body.projectId) ||
      (body.scope === 'workstream' && !body.workstreamId)
    )
      throw new Error('Choose the rule scope')
    const ws = body.workstreamId ? await this.rawWorkstream(body.workstreamId) : undefined
    const projectId = body.projectId ?? ws?.projectId ?? null
    if (projectId) await this.read('project', projectId, projectSchema)
    if (ws && ws.projectId !== projectId) throw new Error('Rule workstream project mismatch')
    const now = Date.now()
    return this.create<Rule>(
      'rule',
      {
        scope: body.scope,
        projectId,
        workstreamId: body.workstreamId ?? null,
        text: body.text,
        provenance: 'user',
        status: 'active',
        evidence: null,
        lastCitedAt: null,
        citeCount: 0,
        createdAt: now,
        updatedAt: now,
      },
      ruleSchema,
    )
  }
  updateRule: Api['updateRule'] = async (id, patch) =>
    this.save(
      'rule',
      { ...(await this.read('rule', id, ruleSchema)), ...patch, updatedAt: Date.now() },
      ruleSchema,
    )
  ideas = async (q: { status?: string; projectId?: string } = {}): Promise<Idea[]> =>
    (await this.records('idea', ideaSchema)).filter(
      (r) => (!q.status || r.status === q.status) && (!q.projectId || r.projectId === q.projectId),
    )
  createIdea: Api['createIdea'] = async (body) => {
    const ws = body.workstreamId ? await this.rawWorkstream(body.workstreamId) : undefined
    const projectId = body.projectId ?? ws?.projectId ?? null
    if (projectId) await this.read('project', projectId, projectSchema)
    if (ws && ws.projectId !== projectId) throw new Error('Idea workstream project mismatch')
    const now = Date.now()
    return this.create(
      'idea',
      {
        text: body.text,
        projectId,
        workstreamId: body.workstreamId ?? null,
        attachments: body.attachments ?? [],
        mode: body.mode ?? 'idea',
        status: 'new',
        createdAt: now,
        updatedAt: now,
      },
      ideaSchema,
    )
  }
  updateIdea: Api['updateIdea'] = async (id, patch) =>
    this.save(
      'idea',
      { ...(await this.read('idea', id, ideaSchema)), ...patch, updatedAt: Date.now() },
      ideaSchema,
    )
  async prepareRun(machineId: string, payload: CommandPayload<'run.start'>) {
    if (!payload.projectId) return payload
    const project = await this.project(payload.projectId),
      config = configOf({ config: project.config! })
    const machine = (await this.machines()).find((m) => m.id === machineId) ?? {
      id: machineId,
      name: machineId,
    }
    // account-projects: a machine with a checkout resolves the path from its own table (the
    // daemon ignores any relayed path). v1 fallback: the key the daemon itself reports.
    const checkedOut = (machine as Machine).checkouts?.some((c) => c.projectId === project.id)
    const key = checkedOut ? undefined : machineConfigKey(config.machines.allowed, machine)
    const repoPath = key ? config.repos.find((r) => r.paths[key])?.paths[key] : undefined
    if (!checkedOut && !repoPath)
      throw Object.assign(
        new Error(
          `${project.name} has no checkout on ${machine.name}. Open the project and choose Add machine.`,
        ),
        { code: 'no_checkout' },
      )
    const workstreams = await this.rawWorkstreams({ projectId: project.id }),
      tasks = await this.rawTasks({ projectId: project.id }),
      rules = await this.rules({ projectId: project.id })
    if (payload.workstreamId && !workstreams.some((w) => w.id === payload.workstreamId))
      throw new Error('Run workstream mismatch')
    if (
      payload.taskId &&
      !tasks.some(
        (t) =>
          t.id === payload.taskId &&
          (!payload.workstreamId || t.workstreamId === payload.workstreamId),
      )
    )
      throw new Error('Run task mismatch')
    const privateProject = PrivateProjectBinding.parse({
      localProjectId: config.project.id,
      snapshot: {
        id: project.id,
        name: project.name,
        ...(repoPath ? { repoPath } : {}),
        workstreams: workstreams.map((w) => ({
          id: w.id,
          name: w.name,
          ...(w.branch ? { branch: w.branch } : {}),
          ...(w.brief ? { brief: w.brief } : {}),
        })),
        tasks: tasks.map((t) => ({
          id: t.id,
          title: t.title,
          status: t.status,
          labels: t.labels,
          dependsOn: t.deps,
          acceptance: t.acceptance.join('\n'),
          ...(t.workstreamId ? { workstreamId: t.workstreamId } : {}),
          ...(t.plan ? { plan: t.plan } : {}), // g18-stack: plan tasks land into the plan branch
        })),
        rules: rules
          .filter(
            (r) =>
              r.status === 'active' && (!r.workstreamId || r.workstreamId === payload.workstreamId),
          )
          .map((r) => ({ id: r.id, text: r.text, scope: r.scope })),
        ...(config.history ? { history: config.history } : {}),
      },
    })
    // g18: a task run gets the planner's model (inside the signed, encrypted payload).
    const task = payload.taskId ? tasks.find((t) => t.id === payload.taskId) : undefined
    return { ...payload, ...taskRunModel(payload, task, config), privateProject }
  }
  /**
   * Mirror a run this device started (or rotated) into the encrypted entities so other trusted
   * devices discover it. Best effort: the run is already started; a lost race only delays them.
   */
  async recordRunStarted(
    payload: Pick<CommandPayload<'run.start'>, 'runId' | 'agent' | 'workstreamId' | 'taskId'>,
    machineId: string,
    status: string,
  ) {
    if (!payload.workstreamId) return
    const ws = await this.rawWorkstream(payload.workstreamId)
    await this.save(
      'workstream',
      {
        ...ws,
        liveRun: { runId: payload.runId, status, machineId, agent: payload.agent },
        updatedAt: Date.now(),
      },
      wsSchema,
    )
    if (!payload.taskId) return
    const task = await this.latestTask(payload.taskId)
    if (task.workstreamId && task.workstreamId !== ws.id) return
    await this.save(
      'task',
      {
        ...task,
        status: 'running',
        runId: payload.runId,
        blockedReason: null,
        updatedAt: Date.now(),
      },
      taskSchema,
    )
  }
  /** Clear the mirrored claim once this device observes the run end; never claims "done". */
  async recordRunEnded(runId: string, outcome: 'finished' | 'failed') {
    for (const ws of await this.rawWorkstreams())
      if (ws.liveRun?.runId === runId)
        await this.save('workstream', { ...ws, liveRun: null, updatedAt: Date.now() }, wsSchema)
    for (const task of await this.rawTasks())
      if (task.runId === runId && (task.status === 'running' || task.status === 'blocked'))
        await this.save(
          'task',
          {
            ...task,
            status: outcome === 'failed' ? 'blocked' : 'review',
            blockedReason: outcome === 'failed' ? 'The run ended with an error' : null,
            updatedAt: Date.now(),
          },
          taskSchema,
        )
  }
  capture: Api['capture'] = async (input) => {
    if (input.files?.length)
      throw new Error(
        'Private file capture requires a selected run; send attachments directly to that run.',
      )
    const target = input.target && input.target !== 'auto' ? input.target : {}
    const idea = await this.createIdea({ text: input.text, mode: input.mode ?? 'idea', ...target })
    return { idea, route: 'triage' }
  }
}

const PRIVATE_METHODS = [
  'projects',
  'project',
  'createProject',
  'updateProject',
  'createAccountProject',
  'recordCheckouts',
  'workstreams',
  'workstream',
  'createWorkstream',
  'updateWorkstream',
  'approveProposed',
  'approvePlan', // g18-batch
  'tasks',
  'task',
  'createTask',
  'updateTask',
  'approveTask',
  'rules',
  'createRule',
  'updateRule',
  'ideas',
  'createIdea',
  'updateIdea',
  'capture',
] as const satisfies readonly (keyof Api & keyof PrivateWork)[]
/** Capture one account-bound adapter per invocation; lock checks precede releasing its result. */
export function installPrivateWork(api: Api, resolve: () => Promise<PrivateWork>) {
  type Method = (...args: unknown[]) => Promise<unknown>
  for (const name of PRIVATE_METHODS) {
    ;(api as unknown as Record<string, Method>)[name] = async (...args) => {
      const work = await resolve()
      const result = await (work[name] as Method)(...args)
      await work.assertOpen()
      return result
    }
  }
  api.triageIdea = async () => {
    throw new Error('Private triage requires an endpoint brain connection')
  }
  api.brainJobs = async () => {
    throw new Error('Private brain queue requires an endpoint brain connection')
  }
}
